import { Injectable, signal, computed } from '@angular/core'
import { environment } from '@environment'
import { ChatSession } from '@models/chat.model'
import { MediaAsset } from '@models/media-asset.model'
import { getDisableGeneralChatFromStorage } from '@utils/storage.utils'

/**
 * Extensions recognized at the end of a name or URL path. Matching against a set
 * rather than "anything after the last dot" keeps a dot inside a title — clip
 * names are truncated descriptions — from being mistaken for an extension.
 */
const KNOWN_EXTENSIONS = new Set([
  'mp4',
  'mov',
  'm4v',
  'webm',
  'mkv',
  'avi',
  'mpg',
  'mpeg',
  'ogv',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'svg',
  'avif',
  'heic',
  'tif',
  'tiff',
  'mp3',
  'wav',
  'aac',
  'm4a',
  'flac',
  'ogg',
  'oga',
  'opus',
  'wma',
  'wmv',
  'flv',
  '3gp',
  '3g2',
  'ts',
  'm2ts',
  'mts',
  'mxf',
  'asf',
  'heif',
  'jfif',
  'ico',
  'aiff',
  'aif',
  'txt',
  'srt',
  'vtt',
  'json',
  'xml',
  'csv',
  'pdf',
  'zip',
])

/** MIME types whose subtype is not the extension people expect. */
const MIME_EXTENSIONS: Record<string, string> = {
  'video/quicktime': 'mov',
  'video/x-matroska': 'mkv',
  'video/x-msvideo': 'avi',
  'video/mpeg': 'mpg',
  'video/x-ms-wmv': 'wmv',
  'video/x-flv': 'flv',
  'video/3gpp': '3gp',
  'video/3gpp2': '3g2',
  'video/mp2t': 'ts',
  'image/jpeg': 'jpg',
  'image/svg+xml': 'svg',
  'image/tiff': 'tif',
  'image/x-icon': 'ico',
  'image/vnd.microsoft.icon': 'ico',
  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'audio/x-wav': 'wav',
  'audio/wav': 'wav',
  'audio/x-aiff': 'aiff',
  'audio/aiff': 'aiff',
  'audio/x-ms-wma': 'wma',
}

/** Last resort, when neither the served bytes nor the URL say what the file is. */
const ASSET_TYPE_EXTENSIONS: Record<string, string> = {
  IMAGE: 'jpg',
  AUDIO: 'mp3',
  VIDEO: 'mp4',
  CLIP: 'mp4',
  MOVIE: 'mp4',
}

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private _disableGeneralChat = signal<boolean>(getDisableGeneralChatFromStorage())

  disableGeneralChat = this._disableGeneralChat.asReadonly()

  refreshDisableGeneralChat(): void {
    this._disableGeneralChat.set(getDisableGeneralChatFromStorage())
  }

  getChatTitle(session: ChatSession): string {
    if (session.mediaAssetId && session.movieName) {
      return session.title ? session.title : session.movieName
    } else if (!session.mediaAssetId) {
      return session.title ? session.title : 'New Chat'
    } else {
      if (session.messages.length > 0) {
        const firstUserMessage = session.messages.find(m => m.role === 'user')
        if (firstUserMessage) {
          return firstUserMessage.content.length > 50
            ? firstUserMessage.content.substring(0, 50) + '...'
            : firstUserMessage.content
        }
      }
    }

    return session.title ? session.title : 'New Chat'
  }

  getAssetUrl(asset: MediaAsset): string {
    if (asset.originalUrl) {
      return asset.originalUrl
    }
    if (asset.userMediaLibraryIngestionRequest) {
      return asset.userMediaLibraryIngestionRequest.assetUrl
    }
    return `${environment.cdnUrl}?id=${asset.id}`
  }

  getThumbnailUrl(asset: MediaAsset): string {
    if (asset.bucketName === 'media-hub-clips' || asset.bucketName === 'media-library') {
      if (asset.generatedThumbnails && asset.generatedThumbnails.length > 0 && asset.generatedThumbnails[0].url) {
        return asset.generatedThumbnails[0].url
      }
    }
    return `${environment.cdnUrl}?id=${asset.id}&t=t`
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) return 'Today'
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days} days ago`

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    })
  }

  formatLongDate(dateData?: string): string {
    if (!dateData) return 'N/A'
    var date: Date = new Date(dateData)

    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  formatDateOrTime(dateString: string): string {
    const date = new Date(dateString)
    const now = new Date()

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const diffDays = Math.floor((nowOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      }) + ' at ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
    }
  }

  formatDuration(seconds?: number): string {
    if (!seconds) return 'N/A'

    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)

    const parts: string[] = []

    if (hours > 0) {
      parts.push(`${hours}h`)
    }
    if (mins > 0) {
      parts.push(`${mins}m`)
    }
    if (secs > 0 || parts.length === 0) {
      parts.push(`${secs}s`)
    }

    return parts.join(' ')
  }

  formatTime(timestamp: string): string {
    const date = new Date(timestamp)
    const now = new Date()

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const diffDays = Math.floor((nowOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24))

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })

    if (diffDays === 0) {
      return timeStr
    } else if (diffDays === 1) {
      return `Yesterday, ${timeStr}`
    } else if (diffDays < 7) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' })
      return `${dayName}, ${timeStr}`
    } else {
      const dateStr = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      })
      return `${dateStr}, ${timeStr}`
    }
  }

  formatVideoTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00'

    // a film's 2:17:28 reads as that, not as 137:28
    const total = Math.floor(seconds)
    const hours = Math.floor(total / 3600)
    const mins = Math.floor((total % 3600) / 60)
    const secs = (total % 60).toString().padStart(2, '0')
    return hours > 0 ? `${hours}:${mins.toString().padStart(2, '0')}:${secs}` : `${mins}:${secs}`
  }

  formatMessageContent(content: string, clickableTimes = false): string {
    let formatted = content.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

    formatted = formatted.replace(/\n\n/g, '<br><br>').replace(/\n/g, '<br>')

    // an answer's HH:MM:SS is a place in the video, and "HH:MM:SS–HH:MM:SS" a stretch of it: a click
    // opens the player there (handled by the page)
    if (clickableTimes) {
      formatted = formatted.replace(
        /\b\d{1,2}:\d{2}:\d{2}(\s*(?:–|—|-|to)\s*\d{1,2}:\d{2}:\d{2})?\b/g,
        (time: string, until?: string) =>
          `<span class="ts-link" role="button" tabindex="0" title="${until ? 'Watch this stretch' : 'Watch this moment'}">${time}</span>`
      )
    }

    return formatted
  }

  hideTooltip(event: Event): void {
    const element = event.currentTarget as HTMLElement
    if (typeof (window as any).bootstrap !== 'undefined') {
      const tooltip = (window as any).bootstrap.Tooltip.getInstance(element)
      if (tooltip) {
        tooltip.hide()
      }
    }
  }

  disposeAllTooltips(): void {
    if (typeof (window as any).bootstrap !== 'undefined') {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        const tooltip = (window as any).bootstrap.Tooltip.getInstance(tooltipTriggerEl)
        if (tooltip) {
          tooltip.dispose()
        }
      })

      const tooltipElements = document.querySelectorAll('.tooltip')
      tooltipElements.forEach(el => el.remove())
    }
  }

  getUniqueName(baseName: string, existingNames: string[]): string {
    if (!existingNames.includes(baseName)) {
      return baseName
    }

    let counter = 1
    let newName = `${baseName} (${counter})`

    while (existingNames.includes(newName)) {
      counter++
      newName = `${baseName} (${counter})`
    }

    return newName
  }

  formatTimeAgo(dateString: string): string {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffSeconds < 60) {
      return diffSeconds === 1 ? '1 sec ago' : `${diffSeconds} secs ago`
    } else if (diffMinutes < 60) {
      return diffMinutes === 1 ? '1 min ago' : `${diffMinutes} mins ago`
    } else if (diffHours < 24) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  async downloadAsset(asset: MediaAsset): Promise<void> {
    const url = this.getAssetUrl(asset)
    if (!url) return

    // Fetch the file as a blob so the browser downloads it instead of opening
    // it in a new tab (the `download` attribute is ignored for cross-origin URLs).
    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error(`Download failed: ${response.status}`)
      const blob = await response.blob()
      this.saveBlob(blob, this.getDownloadFileName(asset, response.headers.get('content-type') || blob.type, url))
    } catch {
      // Fallback when the resource is not CORS-accessible: open directly. The
      // content type is unknowable here, so the name falls back to the URL.
      const link = document.createElement('a')
      link.href = url
      link.download = this.getDownloadFileName(asset, null, url)
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  /**
   * Builds the name a downloaded asset is saved under, always carrying an extension.
   *
   * `asset.name` is a title, not a file name — for generated clips it is a truncated
   * scene description — and `getAssetUrl()` usually points at a query-string CDN
   * endpoint with no extension in its path, so without this the browser saves a bare,
   * extension-less file. The served content type is trusted first because it describes
   * the bytes actually fetched; the URL only ranks above the asset's own type because
   * `originalUrl` can point somewhere the CDN does not serve.
   */
  getDownloadFileName(asset: MediaAsset, contentType?: string | null, url?: string): string {
    const base = this.sanitizeFileName(asset.name || `asset-${asset.id}`)

    if (this.extensionOf(base)) return base

    const identified =
      this.extensionFromMimeType(contentType) ||
      this.extensionFromMimeType(asset.mimeType) ||
      this.extensionOf(this.pathOf(url ?? this.getAssetUrl(asset))) ||
      this.extensionOf(`.${asset.fileMetadata?.format ?? ''}`)

    // Nothing identified the bytes. A name already ending in something extension
    // shaped is more likely to carry a real extension we simply do not list than to
    // be a title, and leaving it beats renaming someone's .wmv to .mp4. Requiring a
    // leading letter is what separates it from a version suffix like "Trailer v2.10".
    if (!identified && /\.[a-z][a-z0-9]{1,4}$/i.test(base)) return base

    return `${base}.${identified || ASSET_TYPE_EXTENSIONS[asset.assetType] || 'mp4'}`
  }

  /** Strips the characters Windows and macOS reject in a file name. */
  sanitizeFileName(name: string): string {
    const cleaned = name
      .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '-')
      .replace(/\s+/g, ' ')
      .replace(/^[.\s-]+|[.\s-]+$/g, '')
      .trim()
    return cleaned || 'download'
  }

  /** The trailing extension of `value`, but only when it is one we recognize. */
  private extensionOf(value: string): string | null {
    const match = /\.([a-z0-9]{2,5})$/i.exec(value)
    const extension = match?.[1].toLowerCase()
    return extension && KNOWN_EXTENSIONS.has(extension) ? extension : null
  }

  private extensionFromMimeType(contentType?: string | null): string | null {
    const type = contentType?.split(';')[0].trim().toLowerCase()
    if (!type || type === 'application/octet-stream') return null
    const mapped = MIME_EXTENSIONS[type] || type.split('/')[1]
    return mapped && KNOWN_EXTENSIONS.has(mapped) ? mapped : null
  }

  /** The path part of a URL, so a `?id=…` query string cannot pose as an extension. */
  private pathOf(url: string): string {
    try {
      return new URL(url, window.location.origin).pathname
    } catch {
      return url.split(/[?#]/)[0]
    }
  }

  /** Downloads a blob that is already in memory, without fetching it again. */
  saveBlob(blob: Blob, filename: string): void {
    const blobUrl = URL.createObjectURL(blob)
    this.triggerDownload(blobUrl, filename)
    URL.revokeObjectURL(blobUrl)
  }

  private triggerDownload(href: string, filename: string): void {
    const link = document.createElement('a')
    link.href = href
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}
